import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import WidgetForm from './WidgetForm';
import EmbedCodeModal from './EmbedCodeModal';
import supabase from '../../lib/supabase';
import { toast } from 'sonner';
import Button from "../Common/Button"

const { FiPlus, FiEdit2, FiTrash2, FiCode, FiToggleLeft, FiToggleRight, FiEye, FiMoreVertical } = FiIcons;

const WidgetsPage = ({ setActiveTab }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [agents, setAgents] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null); // Track which widget's menu is open
  const menuRefs = useRef({}); // Store refs for each menu
  const buttonRefs = useRef({}); // Store refs for each More Actions button

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && menuRefs.current[menuOpen] && !menuRefs.current[menuOpen].contains(event.target) && !buttonRefs.current[menuOpen]?.contains(event.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Fetch widgets and agents
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch widgets
        const { data: widgetsData, error: widgetsError } = await supabase
          .from('widgets_a1b2c3d4e5')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (widgetsError) throw widgetsError;
        setWidgets(widgetsData || []);
        
        // Fetch agents for the dropdown
        const { data: agentsData, error: agentsError } = await supabase
          .from('ai_agents_a1b2c3d4e5')
          .select('id, name')
          .eq('is_active', true);
        
        if (agentsError) throw agentsError;
        setAgents(agentsData || []);
      } catch (error) {
        console.error('Error fetching widgets:', error);
        toast.error(t('widgets.loadError', 'Failed to load widgets. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Set up real-time subscription for widgets changes
    const widgetsChannel = supabase
      .channel('public:widgets_a1b2c3d4e5')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'widgets_a1b2c3d4e5' },
        () => {
          fetchData();
        })
      .subscribe();
      
    return () => {
      supabase.removeChannel(widgetsChannel);
    };
  }, []);  

  const handleCreateWidget = () => {
    setIsCreating(true);
    setEditingWidget(null);
    setMenuOpen(null);
  };
  
  const handleEditWidget = (widget) => {
    setEditingWidget(widget);
    setIsCreating(false);
    setMenuOpen(null);
  };
  
  const handleShowEmbedCode = (widget) => {
    setSelectedWidget(widget);
    setShowEmbedModal(true);
    setMenuOpen(null);
  };
  
  const handleToggleActive = async (widget) => {
    try {
      const { error } = await supabase
        .from('widgets_a1b2c3d4e5')
        .update({ 
          is_active: !widget.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', widget.id);
      
      if (error) throw error;
      
      // Update local state
      setWidgets(widgets.map(w => 
        w.id === widget.id ? { ...w, is_active: !widget.is_active } : w
      ));

      toast.success(widget.is_active ? t('widgets.deactivated', 'Widget deactivated') : t('widgets.activated', 'Widget activated'));
    } catch (error) {
      console.error('Error toggling widget status:', error);
      toast.error(t('widgets.toggleStatusError', 'Failed to update widget status. Please try again.'));
    }
    setMenuOpen(null);
  };
  
  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm(t('widgets.deleteConfirm', 'Are you sure you want to delete this widget? This action cannot be undone.'))) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('widgets_a1b2c3d4e5')
        .delete()
        .eq('id', widgetId);
      
      if (error) throw error;
      
      // Update local state
      setWidgets(widgets.filter(w => w.id !== widgetId));
      toast.success(t('widgets.deleteSuccess', 'Widget deleted successfully'));
    } catch (error) {
      console.error('Error deleting widget:', error);
      toast.error(t('widgets.deleteError', 'Failed to delete widget. Please try again.'));
    }
    setMenuOpen(null);
  };
  
  const handleFormCancel = () => {
    setIsCreating(false);
    setEditingWidget(null);
  };
  
  const handleFormSubmit = (newWidget, isEdit) => {
    if (isEdit) {
      setWidgets(widgets.map(w => 
        w.id === newWidget.id ? newWidget : w
      ));
    } else {
      setWidgets([newWidget, ...widgets]);
    }
    setIsCreating(false);
    setEditingWidget(null);
  };

  const toggleMenu = (widgetId, event) => {
    event.stopPropagation(); // Prevent event from bubbling up
    setMenuOpen(prevMenuOpen => prevMenuOpen === widgetId ? null : widgetId);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-12xl mx-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{t('widgets.title', 'Chat Widgets')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('widgets.subtitle', 'Create and manage chat widgets for your website')}</p>
          </div>
          <Button
            onClick={handleCreateWidget}
            icon={FiPlus}
            responsive
          >
            {t('widgets.createWidget', 'Create Widget')}
          </Button>
        </div>
        
        {/* Widget form */}
        {(isCreating || editingWidget) && (
          <WidgetForm 
            widget={editingWidget}
            agents={agents}
            onCancel={handleFormCancel}
            onSubmit={handleFormSubmit}
            isEdit={!!editingWidget}
          />
        )}
        
        {/* Widgets list */}
        <div className="bg-white dark:bg-gray-800 shadow-xs border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-scroll">
            <table className="table-auto w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider lg:hidden">{t('widgets.actions', 'Actions')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">{t('widgets.actions', 'Actions')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('widgets.name', 'Name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('widgets.agent', 'Agent')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('widgets.status', 'Status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('widgets.allowedOrigins', 'Allowed Origins')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('widgets.themePreview', 'Theme Preview')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex justify-center">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <p className="mt-2">{t('widgets.loadingWidgets', 'Loading widgets...')}</p>
                    </td>
                  </tr>
                ) : widgets.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      {t('widgets.noWidgets', 'No widgets found. Click "Create Widget" to get started.')}
                    </td>
                  </tr>
                ) : (
                  widgets.map((widget) => {
                    const agentName = agents.find(a => a.id === widget.agent_id)?.name || t('widgets.customWebhook', 'Custom Webhook');
                    const themeColor = widget.ui?.primaryColor || '#854fff';
                    
                    return (
                      <tr key={widget.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap relative">
                          {/* Mobile: More Actions Menu */}
                          <div className="lg:hidden">
                            <button
                              ref={(el) => (buttonRefs.current[widget.id] = el)}
                              onClick={(e) => toggleMenu(widget.id, e)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                              title={t('widgets.moreActions', 'More Actions')}
                            >
                              <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                            </button>
                            {menuOpen === widget.id && (
                              <motion.div
                                ref={(el) => (menuRefs.current[widget.id] = el)}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className="fixed z-50 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
                                style={{
                                  top: buttonRefs.current[widget.id]
                                    ? buttonRefs.current[widget.id].getBoundingClientRect().bottom + window.scrollY + 8
                                    : 'auto',
                                  left: buttonRefs.current[widget.id]
                                    ? buttonRefs.current[widget.id].getBoundingClientRect().left + window.scrollX
                                    : 'auto',
                                }}
                              >
                                <div className="py-1 min-w-48">
                                  <Button
                                    whileHover={{ backgroundColor: '#f3f4f6' }}
                                    onClick={() => handleShowEmbedCode(widget)}
                                    className="flex items-center space-x-2 p-2 w-full dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    icon={FiCode}
                                  >
                                    {t('widgets.copyEmbedCode', 'Copy Embed Code')}
                                  </Button>
                                  <Button
                                    whileHover={{ backgroundColor: '#f3f4f6' }}
                                    onClick={() => handleToggleActive(widget)}
                                    className="flex items-center space-x-2 p-2 w-full dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    icon={widget.is_active ? FiToggleRight : FiToggleLeft}
                                  >
                                    {widget.is_active ? t('widgets.deactivate', 'Deactivate') : t('widgets.activate', 'Activate')}
                                  </Button>
                                  <Button
                                    whileHover={{ backgroundColor: '#f3f4f6' }}
                                    onClick={() => handleEditWidget(widget)}
                                    className="flex items-center space-x-2 p-2 w-full dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    icon={FiEdit2}
                                  >
                                    {t('widgets.editWidget', 'Edit Widget')}
                                  </Button>
                                  <Button
                                    whileHover={{ backgroundColor: '#fef2f2' }}
                                    onClick={() => handleDeleteWidget(widget.id)}
                                    className="flex items-center space-x-2 p-2 w-full dark:bg-gray-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    icon={FiTrash2}
                                  >
                                    {t('widgets.deleteWidget', 'Delete Widget')}
                                  </Button>
                                </div>
                              </motion.div>
                            )}
                          </div>
                          {/* Desktop: Action Buttons */}
                          <div className="hidden lg:flex lg:flex-wrap lg:justify-start lg:space-x-2">
                            <button 
                              onClick={() => handleShowEmbedCode(widget)} 
                              className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                              title={t('widgets.copyEmbedCode', 'Copy Embed Code')}
                            >
                              <SafeIcon icon={FiCode} className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => handleToggleActive(widget)} 
                              className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                              title={widget.is_active ? t('widgets.deactivate', 'Deactivate') : t('widgets.activate', 'Activate')}
                            >
                              <SafeIcon icon={widget.is_active ? FiToggleRight : FiToggleLeft} className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => handleEditWidget(widget)} 
                              className="p-1.5 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                              title={t('widgets.editWidget', 'Edit Widget')}
                            >
                              <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => handleDeleteWidget(widget.id)} 
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              title={t('widgets.deleteWidget', 'Delete Widget')}
                            >
                              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-400">{widget.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-400">{agentName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            widget.is_active 
                              ? 'bg-green-100 text-green-800 dark:white dark:text-green-700' 
                              : 'bg-gray-100 text-gray-800 dark:white dark:text-gray-700'
                          }`}>
                            {widget.is_active ? t('widgets.active', 'Active') : t('widgets.inactive', 'Inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {widget.allowed_origins && widget.allowed_origins.length > 0 ? (
                              <div className="max-h-20 overflow-y-auto">
                                {widget.allowed_origins.map((origin, i) => (
                                  <div key={i} className="truncate max-w-xs">{origin}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 italic">{t('widgets.allOrigins', 'All origins')}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div 
                            className="w-8 h-8 rounded-full" 
                            style={{ backgroundColor: themeColor }}
                          ></div>
                        </td>                        
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Embed Code Modal */}
      {showEmbedModal && selectedWidget && (
        <EmbedCodeModal
          widget={selectedWidget}
          onClose={() => setShowEmbedModal(false)}
        />
      )}
    </div>
  );
};

export default WidgetsPage;